import { useEffect, useState } from 'react';
import {
  IlogisticServiceDetails,
  IlogisticServiceDetailsToolsData,
  LOCATION_PLACE_TYPE
} from '../../../../../../../types/services';
import {
  TProductConfigResponse,
  CONFIG_STATUS_NAME,
  CONFIG_CLASS_ID
} from '../../../../../../../types/product.configuration.types';
import LsToolCardToolDataSection from './LsToolCardToolDataSection/LsToolCardToolDataSection';
import ToolDsnActions from './ToolDsnActions/ToolDsnActions';
import NudosDatesLogs from '../../../../../../../components/NudosComponents/NudosDatesLogs/NudosDatesLogs';
import { getConfigurationDisplayElements } from '../../../../../../../utils/logisticServices.utils';
import { IdateLog } from '../../../../../../../types/global';
import { ToolConfigurationsSection } from '../../../../../../../components';
import { isProductConfigurable } from 'utils/configurations';
import './LogisticServiceToolCard.scss';

const LogisticServiceToolCard = ({
  toolData,
  logisticServiceData,
  updateDataCallback
}: {
  toolData: IlogisticServiceDetailsToolsData;
  logisticServiceData?: IlogisticServiceDetails | null;
  updateDataCallback: () => Promise<void>;
}) => {
  const [configurationsDates, setConfigurationsDates] = useState<IdateLog[]>();
  const { productConfigs, category, lastInspectionDate, requireInspection = false } = toolData;
  const { destinationLocation, originLocation } = logisticServiceData || {};

  const datesTitle = 'Historial de la herramienta';
  const hasDatesToShow = !!configurationsDates?.length;
  const originIsWarehouse = originLocation?.place === LOCATION_PLACE_TYPE.NUDOS;
  const destinationIsWarehouse = destinationLocation?.place === LOCATION_PLACE_TYPE.NUDOS;
  const originOrDestinationIsWarehouse = originIsWarehouse || destinationIsWarehouse;
  const showExternalWipingSection = !!productConfigs?.some(config => config?.configClassId === CONFIG_CLASS_ID.WIPING);
  const hasConfigurations = !!productConfigs?.length;

  const configurationsData = {
    productId: Number(toolData.productId),
    configurations: productConfigs || [],
    hasConfigurations,
    logisticServiceId: logisticServiceData?.id || undefined
  };

  const toolCouldHaveConfigurations = isProductConfigurable(category);
  const showConfigurationsSection = originOrDestinationIsWarehouse && toolCouldHaveConfigurations && hasConfigurations;

  const getConfigurationsDates = () =>
    (productConfigs as TProductConfigResponse[])?.reduce<{ label: string; date: string }[]>(
      (allConfigurationsDates, thisConfiguration) => {
        const { text = 'Sin nombre' } = getConfigurationDisplayElements(thisConfiguration);
        const statusLogs = thisConfiguration?.statusLog || [];
        // Usar statusLog para obtener las fechas
        const thisConfigurationDates = statusLogs
          .filter(log => log.configStatus !== CONFIG_STATUS_NAME.TO_COMPLETE)
          .map(log => ({
            label: `${text} - ${log?.configStatus?.toLowerCase() || 'Sin estado'}`,
            date: log.createdAt ? new Date(log.createdAt).toISOString() : 'Sin datos'
          }));
        return allConfigurationsDates.concat(thisConfigurationDates);
      },
      []
    );

  useEffect(() => {
    if (!productConfigs?.length) return;
    const allDates = getConfigurationsDates();
    setConfigurationsDates(allDates);
  }, [productConfigs]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="logisticServiceToolCard">
      <div className="topSection">
        <LsToolCardToolDataSection
          toolData={toolData}
          logisticServiceData={logisticServiceData}
          updateDataCallback={updateDataCallback}
          organizationInfo={logisticServiceData?.organization}
        />
        {hasDatesToShow && <NudosDatesLogs title={datesTitle} dates={configurationsDates || []} />}
      </div>

      {showConfigurationsSection && <div className="sectionDivider" />}

      {showConfigurationsSection && (
        <ToolConfigurationsSection
          showExternalWipingSection={showExternalWipingSection}
          updateDataCallback={updateDataCallback}
          configurationsData={configurationsData}
        />
      )}

      {requireInspection && (
        <ToolDsnActions
          logisticServiceId={logisticServiceData?.id}
          inspectionDate={lastInspectionDate}
          logisticServiceStatusName={logisticServiceData?.statusName}
        />
      )}
    </div>
  );
};

export default LogisticServiceToolCard;
